import { useEffect, useMemo, useRef, useState } from "react";
import {
  RequestCreateInboundPlanRequest,
  RequestCreateInboundPlansRequest,
  RequestGetAmazonItemsListRequest,
  RequestGetShippingSourceInformationRequest,
  RequestItem,
  RequestRegistPlanRequest,
  RequestUpsertShippingSourceInformationRequest,
  ResponseAmazonItems,
  ResponseGetShippingSourceInformationResponse,
} from "../../services/OpenAPI";
import { useApiGetAmazonItems } from "../api/useApiGetAmazonItems";
import { useApiUpdateAmazonItemsStatus } from "../api/useApiUpdateAmazonItemsStatus";
import { formatDate } from "../../utils/dateFormat";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { isGeneralUser, isNotGeneralUser } from "../../constants/roles";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { userSearchOptionsSelector } from "../../recoil/selectors/userSearchOptionsSelector";
import {
  decodePickupCsv,
  parsePickupCsv,
  PickupGroupInfo,
  PickupProduct,
  PickupTransportInfo,
} from "../../utils/parsePickupCsv";
import { useApiRegisterPlans } from "../api/useApiRegisterPlans";
import { pendingPlanUpdatedFlagState } from "../../recoil/atoms/pendingPlanAtoms";
import { ShippingSourceInfoItem } from "../../components/CreateInboundShipmentPlanDialog";
import { useApiRegisterInboundPlan } from "../api/useApiRegisterInboundPlan";
import { PendingPlanItem } from "../../components/pendingPlan/PendingPlanTable";
import { useApiGetShippingSourceInformation } from "../api/useApiGetShippingSourceInformation";
import { useApiUpsertShippingSourceInformation } from "../api/useApiUpsertShippingSourceInformation";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { prefectureList } from "../../constants/prefectureName";
import { countryCodeList } from "../../constants/countryCode";
import { Order } from "../../components/pendingReceipt/PendingReceiptTable";
import { Status } from "../../constants/status";

export type SearchGenreOption = {
  id: string;
  label: string;
};

export const usePendingPlan = () => {
  const searchGenres: SearchGenreOption[] = [
    { id: "janCode", label: "JAN" },
    { id: "asin", label: "ASIN" },
    { id: "itemName", label: "商品名" },
    { id: "fnsku", label: "FNSKU" },
    { id: "sku", label: "SKU" },
  ];
  const inputRef = useRef<HTMLInputElement>(null);

  const { fetchAmazonItems } = useApiGetAmazonItems();
  const { updateAmazonItemsStatus } = useApiUpdateAmazonItemsStatus();
  const { registerPlans, resultRegister } = useApiRegisterPlans();
  const { registerInboundPlan, isLoading, error, setError } =
    useApiRegisterInboundPlan();
  const { fetchShippingSourceInformation } =
    useApiGetShippingSourceInformation();
  const { upsertShippingSourceInformation } =
    useApiUpsertShippingSourceInformation();
  const loginUser = useRecoilValue(loginUserSelector);
  const userSearchOptions = useRecoilValue(userSearchOptionsSelector);
  const setPendingPlanUpdatedFlag = useSetRecoilState(
    pendingPlanUpdatedFlagState
  );
  const pendingPlanUpdatedFlag = useRecoilValue(pendingPlanUpdatedFlagState);
  const resetPendingPlanUpdatedFlag = useResetRecoilState(
    pendingPlanUpdatedFlagState
  );

  const [pendingPackagingList, setPendingPackagingList] = useState<
    PendingPlanItem[]
  >([]);
  const [totalListCount, setTotalListCount] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [searchKeyword, setSearchKeyword] = useState("");
  const [janCode, setJanCode] = useState("");
  const [asin, setAsin] = useState("");
  const [itemName, setItemName] = useState("");
  const [fnsku, setFnsku] = useState("");
  const [sku, setSku] = useState("");
  const [expectedCount, setExpectedCount] = useState<number | string>("");
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState<PendingPlanItem | null>(
    null
  );
  const selectedChildren = useRef<PendingPlanItem[]>([]);
  const [checkItems, setCheckItems] = useState<PendingPlanItem[]>([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const [toastCloseTime, setToastCloseTime] = useState<number | null>(null);
  const handleCloseToast = () => setToastOpen(false);
  const [checkFnskuCode, setCheckFnskuCode] = useState("");
  const [checkJanCode, setCheckJanCode] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [shippingInformation, setShippingInformation] =
    useState<ResponseGetShippingSourceInformationResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const refSortKey = useRef<number>(1);
  const refDirection = useRef<number>(-1);

  const displayList = useMemo(() => {
    return pendingPackagingList
      .filter((item) => item.isParent)
      .map((parent) => {
        const children = pendingPackagingList.filter(
          (child) => child.parentId === parent.id
        );
        const janCodes = [
          parent.janCode || "",
          ...children.map((child) => child.janCode || ""),
        ]
          .filter((code) => code !== "")
          .join("\n"); //改行で結合

        return {
          ...parent,
          janCode: janCodes,
        };
      });
  }, [pendingPackagingList]);

  // チェックされたIDの更新ハンドラ
  const handleCheckChange = (newChecked: Set<string>) => {
    setCheckedIds(newChecked);
  };

  const [selectedGenreId, setSelectedGenreId] = useState(
    searchGenres[0]?.label ?? ""
  );

  const resetGenreWord = () => {
    setJanCode("");
    setAsin("");
    setItemName("");
    setFnsku("");
    setSku("");
    setSearchKeyword("");
    return new Promise<void>((resolve) => {
      setJanCode("");
      setAsin("");
      setItemName("");
      setFnsku("");
      setSku("");
      setSearchKeyword("");

      // 状態更新の直後に resolve（1フレーム後に確実に）
      requestAnimationFrame(() => resolve());
    });
  };

  const resetFilter = async () => {
    if (isNotGeneralUser(loginUser?.permissionId)) {
      setSelectedUserId(null);
    }
    await resetGenreWord();
    setPendingPlanUpdatedFlag(true);
    return new Promise<void>((resolve) => {
      if (isNotGeneralUser(loginUser?.permissionId)) {
        setSelectedUserId(null);
      }
    });
  };

  const handleGenreChange = (genreId: string) => {
    setSelectedGenreId(genreId);
    resetGenreWord();
  };

  const resetCheckCode = () => {
    setCheckFnskuCode("");
    setCheckJanCode("");
  };

  const transformData = (data: ResponseAmazonItems[]): PendingPlanItem[] => {
    const grouped = new Map<number, ResponseAmazonItems[]>();
    // amazon_item_id ごとにグルーピング
    data.forEach((item) => {
      const key = item.amazonItemId!;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    });

    const result: PendingPlanItem[] = [];

    grouped.forEach((items, amazonItemId) => {
      // グループ内に親データ相当の1行があると仮定（amazon_接頭辞のカラムが使える）
      const first = items[0];

      if (items.length === 1) {
        // 子のみの場合（親なし）
        const setNum = first.amazonSetNum ?? 0;
        const arriveNum = first.amazonArriveNum ?? 0;
        result.push({
          id: `only-${amazonItemId}`,
          isParent: true,
          inputDate: formatDate(first.inputDate),
          user: first.userName,
          itemName: first.itemName,
          janCode: first.janCode,
          expiryDate: formatDate(first.parentExpiryDate),
          setCount: setNum,
          deliveryCount: arriveNum,
          deliveryUnit: first.deliveryUnit,
          expectedCount: setNum * arriveNum,
          shippedCount: first.amazonActualShippingNum,
          receivedCount: first.amazonSumNum,
          asin: first.asin,
          sku: first.sku,
          fnsku: first.fnskuCode,
          stickerNum: first.stickerNum,
          returnNum: first.returnNum,
          destructionNum: first.destructionNum,
          storageLocation: first.storageLocation,
          statusId: first.statusId,
          baseId: Number(first.baseId ?? 0),
          userId: Number(first.userId ?? 0),
          tenantId: Number(first.tenantId ?? 0),
          memo: first.memo,
          image: first.imageUrl,
        });
      } else {
        // 親ありの場合
        const setNum = first.amazonSetNum ?? 0;
        const arriveNum = first.amazonArriveNum ?? 0;
        result.push({
          id: `parent-${amazonItemId}`,
          isParent: true,
          inputDate: formatDate(first.inputDate),
          user: first.userName,
          itemName: first.amazonItemName ?? "",
          janCode: "", // 空欄
          expiryDate: formatDate(first.parentExpiryDate),
          setCount: setNum,
          deliveryCount: arriveNum,
          deliveryUnit: first.deliveryUnit,
          expectedCount: setNum * arriveNum,
          shippedCount: first.amazonActualShippingNum,
          receivedCount: first.amazonSumNum,
          asin: first.asin,
          sku: first.sku,
          fnsku: first.fnskuCode,
          stickerNum: first.stickerNum,
          returnNum: first.returnNum,
          destructionNum: first.destructionNum,
          storageLocation: first.storageLocation,
          statusId: first.statusId,
          baseId: Number(first.baseId ?? 0),
          userId: Number(first.userId ?? 0),
          tenantId: Number(first.tenantId ?? 0),
          memo: first.memo,
          image: first.imageUrl,
        });

        // 子データ（通常カラム）
        items.forEach((child, idx) => {
          const setNum = child.setNum ?? 0;
          const arriveNum = child.arriveNum ?? 0;
          result.push({
            id: `child-${amazonItemId}-${idx}`,
            parentId: `parent-${amazonItemId}`,
            inputDate: "",
            user: child.userName,
            itemName: child.itemName,
            janCode: child.janCode,
            expiryDate: formatDate(child.expiryDate),
            setCount: setNum,
            deliveryCount: arriveNum,
            expectedCount: setNum * arriveNum,
            shippedCount: child.actualShippingNum,
            receivedCount: child.sumNum,
            asin: child.asin,
            sku: child.sku,
            fnsku: child.fnskuCode,
            stickerNum: child.stickerNum,
            returnNum: child.returnNum,
            destructionNum: child.destructionNum,
            storageLocation: child.storageLocation,
            baseId: Number(first.baseId ?? 0),
            userId: Number(first.userId ?? 0),
            tenantId: Number(first.tenantId ?? 0),
          });
        });
      }
    });

    return result;
  };

  const handleParentCheck = (parentId: string, childIds: string[]) => {
    // const newSet = new Set<string>([parentId, ...childIds]);
    const newSet = new Set<string>([parentId]);
    handleCheckChange(newSet);
  };

  const handleKeywordChange = (keyword: string) => {
    // 一致する親行を探す（parentId が undefined or isParent が true のもの）
    const matchedParent = pendingPackagingList.find(
      (item) => item.janCode === keyword && item.isParent
    );

    if (matchedParent && selectedGenreId === searchGenres[0].id) {
      searchItems(keyword);
      inputRef.current?.focus();

      // 対象親の子IDリストを取得
      const childIds = pendingPackagingList
        .filter((child) => child.parentId === matchedParent.id)
        .map((child) => String(child.id));

      // handleParentCheck でチェック更新（関数を PendingReceipt で定義する）
      handleParentCheck(String(matchedParent.id), childIds);
    } else {
      // 該当なしならチェックをすべて解除
      handleCheckChange(new Set());
    }
  };

  const focusInputField = () => {
    inputRef.current?.focus();
  };

  const handleRowClick = (item: PendingPlanItem) => {
    if (!item.isParent) return;

    const children = pendingPackagingList.filter(
      (row) => row.parentId === item.id
    );
    setSelectedParent(item);
    selectedChildren.current = children;
    setDetailDialogOpen(true);
  };

  // const handleStatusChange = (id: number) => {
  //   setSelectedStatusId(id);
  // };

  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
    // ここでフィルタ処理やAPI呼び出し
  };

  const userOptions = useMemo(() => {
    return (
      userSearchOptions?.map((user) => ({
        label: `${user.userNumber ?? ""}_${user.lastName ?? ""}${
          user.firstName ?? ""
        }`,
        id: user.id!,
      })) ?? []
    );
  }, [userSearchOptions]);

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const fetchData = async (req: RequestGetAmazonItemsListRequest) => {
    const response = await fetchAmazonItems(req);
    if (response) {
      const transformed = transformData(response.items ?? []);
      setTotalListCount(response.totalCount ?? 0);
      setPendingPackagingList(transformed);
    }
  };

  useEffect(() => {
    if (loginUser) {
      if (loginUser.tenantId && loginUser.id) {
        const req: RequestGetAmazonItemsListRequest = {
          statusId: 3,
          tenantId: loginUser.tenantId,
          userId: loginUser.id,
          createdUserId:
            isGeneralUser(loginUser?.permissionId) ? loginUser.id! : undefined,
          page,
          pageSize: limit,
          sortKey: refSortKey.current,
          direction: refDirection.current,
        };
        fetchData(req);
      }
      if (isGeneralUser(loginUser?.permissionId)) {
        setSelectedUserId(loginUser.id!);
      }
    }
  }, [loginUser]);

  useEffect(() => {
    if (pendingPlanUpdatedFlag) {
      if (loginUser?.tenantId && loginUser.id) {
        const req: RequestGetAmazonItemsListRequest = {
          statusId: 3,
          tenantId: loginUser.tenantId,
          userId: loginUser.id,
          createdUserId:
            isGeneralUser(loginUser?.permissionId) ? loginUser.id! : undefined,
          page,
          pageSize: limit,
          sortKey: refSortKey.current,
          direction: refDirection.current,
        };
        fetchData(req);
      }
      resetPendingPlanUpdatedFlag();
    }
  }, [pendingPlanUpdatedFlag]);

  useEffect(() => {
    switch (selectedGenreId) {
      case searchGenres[0].label:
        setJanCode(searchKeyword);
        break;
      case searchGenres[1].label:
        setAsin(searchKeyword);
        break;
      case searchGenres[2].label:
        setItemName(searchKeyword);
        break;
      case searchGenres[3].label:
        setFnsku(searchKeyword);
        break;
      case searchGenres[4].label:
        setSku(searchKeyword);
        break;
    }
    handleKeywordChange(searchKeyword);
  }, [searchKeyword]);

  const searchItems = (searchKeyword: string) => {
    if (loginUser?.tenantId && loginUser.id) {
      const req: RequestGetAmazonItemsListRequest = {
        statusId: 3,
        tenantId: loginUser.tenantId,
        userId: loginUser.id,
        createdUserId: selectedUserId ?? undefined,
        janCode:
          searchGenres[0].id && janCode !== "" ? searchKeyword : undefined,
        asin: searchGenres[1].id && asin !== "" ? searchKeyword : undefined,
        amazonItemName:
          searchGenres[2].id && itemName !== "" ? searchKeyword : undefined,
        fnskuCode:
          searchGenres[3].id && fnsku !== "" ? searchKeyword : undefined,
        sku: searchGenres[4].id && sku !== "" ? searchKeyword : undefined,
        page,
        pageSize: limit,
        sortKey: refSortKey.current,
        direction: refDirection.current,
      };
      fetchData(req);
    }
  };

  const handlePageChange = (newPage: number, newLimit: number) => {
    if (loginUser?.tenantId && loginUser.id) {
      setPage(newPage);
      setLimit(newLimit);

      const req: RequestGetAmazonItemsListRequest = {
        statusId: 3,
        tenantId: loginUser.tenantId,
        userId: loginUser.id,
        createdUserId: isGeneralUser(loginUser.permissionId) ? loginUser.id : undefined,
        page: newPage + 1,
        pageSize: newLimit,
        sortKey: refSortKey.current,
        direction: refDirection.current,
      };
      fetchData(req);
    }
  };

  // ファイル選択ダイアログを開く
  const handleOpenFileDialog = () => {
    fileInputRef.current?.click();
  };

  const showUploadError = (message: string) => {
    setToastMessage(message);
    setToastSeverity("error");
    setToastOpen(true);
  };

  // ファイル選択後に読み込む　（こっちは使わなくなる）
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 同じファイルを続けて選んでも onChange が発火するようにリセットする
    e.target.value = "";
    if (!file) return;

    if (!loginUser?.tenantId || !loginUser?.baseId || !loginUser?.id) {
      showUploadError("ログイン情報を取得できませんでした。再ログインしてください。");
      return;
    }
    if (!shippingInformation) {
      showUploadError(
        "配送元情報が未登録です。「プラン作成」から配送元情報を登録してください。"
      );
      return;
    }

    // セラーセントラルのCSVやExcelで保存し直したCSVは Shift_JIS のことがあるため判別して読む
    const text = decodePickupCsv(await file.arrayBuffer());
    const parsedData = parsePickupCsv(text);

    // 必要ならステートに保存したり、何かの処理をする
    const groupInfo: PickupGroupInfo = parsedData.groupInfo;
    const products: PickupProduct[] = parsedData.products;
    const transportInfo: PickupTransportInfo = parsedData.transportInfo;

    if (!groupInfo.planName || products.length === 0) {
      showUploadError(
        "ピッキングリストの形式を読み取れませんでした。セラーセントラルからダウンロードしたCSVをそのままアップロードしてください。"
      );
      return;
    }

    // 画面に表示中のページだけで照合すると、別ページにある商品が黙って漏れるため、
    // プラン作成待ちの商品を全件取得して照合する
    const allResponse = await fetchAmazonItems({
      statusId: Status.PendingPlan,
      tenantId: loginUser.tenantId,
      userId: loginUser.id,
      createdUserId: isGeneralUser(loginUser.permissionId)
        ? loginUser.id
        : undefined,
      page: 1,
      pageSize: 10000,
      sortKey: 1,
      direction: -1,
    });
    if (!allResponse) {
      showUploadError("プラン作成待ちの商品の取得に失敗しました。");
      return;
    }
    // ユーザーで絞り込み中はそのユーザーの商品だけを対象にする
    const candidates = transformData(allResponse.items ?? []).filter(
      (item) =>
        item.isParent && (selectedUserId == null || item.userId === selectedUserId)
    );

    const matchedIds: number[] = [];
    const unmatchedSkus: string[] = [];
    const duplicatedSkus: string[] = [];
    Array.from(new Set(products.map((p) => p.sku.trim()))).forEach((sku) => {
      const hits = candidates.filter((item) => item.sku?.trim() === sku);
      if (hits.length === 0) {
        unmatchedSkus.push(sku);
      } else if (hits.length > 1) {
        duplicatedSkus.push(sku);
      } else {
        matchedIds.push(Number(String(hits[0].id).split("-")[1]));
      }
    });

    if (duplicatedSkus.length > 0) {
      showUploadError(
        `同じSKUの商品がプラン作成待ちに複数あるため、紐付け先を特定できません。ユーザーで絞り込んでから再度アップロードしてください。（${duplicatedSkus.join(", ")}）`
      );
      return;
    }
    if (matchedIds.length === 0) {
      showUploadError(
        `一致する商品がプラン作成待ちに存在しません。SKUをご確認ください。（${unmatchedSkus.join(", ")}）`
      );
      return;
    }
    if (
      unmatchedSkus.length > 0 &&
      !window.confirm(
        `以下のSKUはプラン作成待ちに見つかりませんでした。\n${unmatchedSkus.join("\n")}\n\n一致した${matchedIds.length}件の商品のみでプランを登録しますか？`
      )
    ) {
      return;
    }

    // 都道府県と国名のラベルを取得
    const matchedPrefecture = prefectureList.find(
      (p) => p.id === shippingInformation.prefectureId
    );
    const matchedCountry = countryCodeList.find(
      (c) => c.id === shippingInformation.countryId
    );

    const req: RequestRegistPlanRequest = {
      tenantId: loginUser?.tenantId,
      baseId: loginUser?.baseId,
      itemIds: matchedIds,
      groupNum: Number(groupInfo.group),
      skuCount: Number(groupInfo.sku),
      boxCount:
        groupInfo.productCount && groupInfo.productCount !== ""
          ? Number(groupInfo.productCount)
          : undefined,
      boxHeight:
        transportInfo.boxHeight && transportInfo.boxHeight !== ""
          ? Number(transportInfo.boxHeight)
          : undefined,
      boxLength:
        transportInfo.boxLength && transportInfo.boxLength !== ""
          ? Number(transportInfo.boxLength)
          : undefined,
      boxWidth:
        transportInfo.boxWidth && transportInfo.boxWidth !== ""
          ? Number(transportInfo.boxWidth)
          : undefined,
      boxWeight:
        transportInfo.boxWeight && transportInfo.boxWeight !== ""
          ? Number(transportInfo.boxWeight)
          : undefined,
      name: groupInfo.planName,
      shipNum: "",
      userId: loginUser.id,
      countryCode: matchedCountry?.label ?? "",
      postalCode: shippingInformation.postalCode ?? "",
      stateOrProvinceCode: matchedPrefecture?.label ?? "",
      city: shippingInformation.city ?? "",
      addressLine1: shippingInformation.addressLine1 ?? "",
      senderName: shippingInformation.name ?? "",
      phoneNumber: shippingInformation.phoneNumber ?? "",
    };

    const response = await registerPlans(req);
    if (response) {
      setToastMessage("プラン作成に成功しました");
      setToastSeverity("success");
      setToastOpen(true);
      setPendingPlanUpdatedFlag(true);
    } else {
      setToastMessage("プラン作成に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleDownloadManifestExcelByUser = () => {
    // --- userId ごとに grouping ---
    const userMap = new Map<number, { userName: string; rows: any[] }>();

    Array.from(checkedIds).forEach((rawId) => {
      const idNum = Number(rawId.split("-")[1]);
      const matched = pendingPackagingList.find((item) => {
        const itemIdNum = Number(String(item.id).split("-")[1]);
        return itemIdNum === idNum && item.isParent;
      });

      if (!matched || !matched.userId) return;

      const sku = matched.sku ?? "";
      const quantity = (matched.receivedCount ?? 0) / (matched.setCount ?? 1);
      const expiry = matched.expiryDate
        ? new Date(matched.expiryDate).toLocaleDateString("en-US")
        : "";

      if (!userMap.has(matched.userId)) {
        userMap.set(matched.userId, {
          userName: matched.user ?? `User_${matched.userId}`,
          rows: [],
        });
      }

      const rows = userMap.get(matched.userId)!.rows;
      const existing = rows.find((r) => r[0] === sku && r[4] === expiry); // SKU + Expiry でまとめる

      if (existing) {
        existing[1] += quantity; // 数量を合計
      } else {
        rows.push([sku, quantity, "", "", expiry]);
      }
    });

    if (userMap.size === 0) {
      alert("出力対象の商品がありません。");
      return;
    }

    // --- 各ユーザーごとに Excel 生成 ---
    userMap.forEach((data, userId) => {
      const wb = XLSX.utils.book_new();

      const wsData = [
        [], // 1行目空
        [], // 2行目空
        ["Default prep owner", "Seller"], // 3行目
        ["Default labeling owner", "Seller"], // 4行目
        [], // 5行目空
        [], // 6行目空
        ["", "", "任意"], // 7行目
        [
          "Merchant SKU",
          "Quantity",
          "Prep owner",
          "Labeling owner",
          "Expiration date (MM/DD/YYYY)",
        ], // 8行目
        ...data.rows.map((r) => [r[0], r[1], r[2], r[3], r[4]]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [
        { wch: 20 },
        { wch: 10 },
        { wch: 15 },
        { wch: 15 },
        { wch: 25 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Create workflow – template");

      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // ファイル名：ManifestFileUpload_Template_IncludeExpirationDate_MPL[(ユーザー名)].xlsx
      const safeUserName = data.userName.replace(/[\\/:*?"<>|]/g, "_");
      const fileName = `ManifestFileUpload_Template_IncludeExpirationDate_MPL[${safeUserName}].xlsx`;

      saveAs(blob, fileName);
    });
  };

  const handleCreatePlan = async (
    shippingSourceInfo: ShippingSourceInfoItem
  ) => {
    const productIdToNameMap = new Map<number, string>();
    pendingPackagingList.forEach((product) => {
      productIdToNameMap.set(
        Number(product.id.toString().split("-")[1]),
        product.itemName ?? ""
      );
    });

    const plansMap = new Map<
      number,
      { tenantId: number; items: RequestItem[] }
    >();

    // チェックされたIDから必要なアイテムを抽出して userId ごとにグルーピング
    Array.from(checkedIds).forEach((rawId) => {
      const idNum = Number(rawId.split("-")[1]);
      const matched = pendingPackagingList.find((item) => {
        const itemIdNum = Number(String(item.id).split("-")[1]);
        return itemIdNum === idNum && item.isParent;
      });

      if (!matched || !matched.userId) return;

      const formattedExpiryDate = matched.expiryDate
        ? matched.expiryDate.split("/").join("-")
        : undefined;

      const item: RequestItem = {
        id: idNum,
        msku: matched.sku ?? "",
        quantity: (matched.receivedCount ?? 0) / (matched.setCount ?? 1),
        expirationDate: formattedExpiryDate,
      };

      // userId ごとにアイテムをまとめる
      if (!plansMap.has(matched.userId)) {
        plansMap.set(matched.userId, { tenantId: matched.tenantId, items: [] });
      }
      plansMap.get(matched.userId)!.items.push(item);
    });

    // plans の配列を作成
    const plans: RequestCreateInboundPlanRequest[] = Array.from(
      plansMap.entries()
    ).map(([userId, { tenantId, items }]) => ({
      baseId: loginUser!.baseId ?? 0,
      tenantId: tenantId,
      countryCode: shippingSourceInfo.countryCode,
      postalCode: shippingSourceInfo.postalCode,
      stateOrProvinceCode: shippingSourceInfo.stateOrProvince,
      city: shippingSourceInfo.city,
      addressLine1: shippingSourceInfo.addressLine1,
      name: shippingSourceInfo.name,
      phoneNumber: shippingSourceInfo.phoneNumber,
      items,
      createdUserId: userId, // ← userId ごとに設定
    }));

    const req: RequestCreateInboundPlansRequest = {
      plans,
    };
    const response = await registerInboundPlan(req);

    if (response) {
      if (!response.failed) {
        // 成功時トースト
        setToastMessage("プラン作成に成功しました");
        setToastSeverity("success");
        setToastOpen(true);
        setCreatePlanDialogOpen(false);
        setPendingPlanUpdatedFlag(true);
        setToastCloseTime(4000);
      } else {
        // 失敗時処理
        if (response && response.failed) {
          // 出品者ごとに「未連携が原因の失敗」と「それ以外の失敗」を分ける。
          // 未連携は本人に連携を依頼してもらう必要があるため、氏名を明示して別枠で案内する。
          const notConnectedOwnerNames = Array.from(
            new Set(
              response.failed
                .filter((fail) => fail.errMsg?.includes("LWA_NOT_CONNECTED"))
                .map(
                  (fail) =>
                    pendingPackagingList.find(
                      (item) => Number(item.userId) === Number(fail.userId)
                    )?.user ?? ""
                )
            )
          ).filter((name) => name !== "");

          const otherFailures = response.failed.filter(
            (fail) => !fail.errMsg?.includes("LWA_NOT_CONNECTED")
          );

          // それ以外の失敗の商品名一覧
          const otherFailedProductNames: string[] = [];
          otherFailures.forEach((fail) => {
            fail.items?.forEach((item) => {
              const productName = productIdToNameMap.get(item.id ?? -1);
              otherFailedProductNames.push(productName ?? `商品ID: ${item.id}`);
            });
          });

          const otherReasons = otherFailures
            .map((fail) => {
              if (fail.errMsg?.includes("Expiration date required")) {
                return "・賞味期限の入力が必須の商品があります。";
              } else if (
                fail.errMsg?.includes("The following MSKUs are not valid")
              ) {
                return "・SKUが正しいか確認してください。";
              } else if (
                fail.errMsg?.includes(
                  "Approval is required before this item can be sent to Amazon."
                )
              ) {
                return "・Amazonの承認が必要な商品があります。";
              } else if (
                fail.errMsg?.includes(
                  "requires prepOwner but NONE was assigned"
                )
              ) {
                return "・準備（Prep）が必要な商品があります。\n　セラーセントラルで「準備作業の担当者」を「SELLER」に設定してください";
              } else if (
                fail.errMsg?.includes(
                  "Member must have value greater than or equal to 1"
                )
              ) {
                return "・実入荷総数が0の商品があります。1以上の値を入力してください";
              }
              return fail.errMsg;
            })
            .join("\n");

          // 成功したプランがあるか（部分成功の判定）
          const hasSuccess = (response.success?.length ?? 0) > 0;

          const sections: string[] = [];

          // 見出し：成功が1件でもあれば「一部作成」、無ければ「失敗」
          sections.push(
            hasSuccess
              ? "一部のプランを作成しました。"
              : "プラン作成に失敗しました。"
          );

          // 未連携の出品者（本人に連携を依頼）
          if (notConnectedOwnerNames.length > 0) {
            sections.push(
              `【Amazon未連携のため作成できませんでした】\n` +
                notConnectedOwnerNames.map((name) => `・${name}さん`).join("\n") +
                `\n→ 上記の方にAmazon連携（ユーザー情報画面）を依頼してください。`
            );
          }

          // その他の理由による失敗
          if (otherFailedProductNames.length > 0) {
            sections.push(
              `【下記の商品は作成できませんでした】\n` +
                otherFailedProductNames.map((name) => `■${name}`).join("\n") +
                `\n\n理由：\n${otherReasons}`
            );
          }

          setToastMessage(sections.join("\n\n"));
        } else {
          setToastMessage(
            `以下の商品のプラン作成に失敗しました。\n商品名を取得できませんでした。`
          );
        }

        // 部分成功でも失敗理由を確実に読ませるため error 扱いのまま自動クローズしない
        setToastSeverity("error");
        setToastOpen(true);
        setCreatePlanDialogOpen(false);
        setPendingPlanUpdatedFlag(true);
        setToastCloseTime(null);
      }
    } else {
      // let msg =
      //   "プラン作成に失敗しました。Amazonの承認が必要な商品が含まれていないか確認してください。";
      // if (error?.includes("Expiration date required")) {
      //   msg = "プラン作成に失敗しました。賞味期限の入力が必須です。";
      // } else if (error?.includes("The following MSKUs are not valid")) {
      //   msg = "プラン作成に失敗しました。SKUが正しいか確認してください。";
      // } else if (
      //   error?.includes(
      //     "Approval is required before this item can be sent to Amazon."
      //   )
      // ) {
      //   msg = "プラン作成に失敗しました。Amazonの承認が必要な商品があります。";
      // }
      // setToastMessage(msg);
      // // setToastMessage("プラン作成に失敗しました。");
      // setToastSeverity("error");
      // setToastOpen(true);
    }
  };

  useEffect(() => {
    if (!error) return;
    let msg = "プラン作成に失敗しました。";
    if (error?.includes("Expiration date required")) {
      msg = "プラン作成に失敗しました。賞味期限の入力が必須です。";
    } else if (error?.includes("The following MSKUs are not valid")) {
      msg = "プラン作成に失敗しました。SKUが正しいか確認してください。";
    } else if (
      error?.includes(
        "Approval is required before this item can be sent to Amazon."
      )
    ) {
      msg = "プラン作成に失敗しました。Amazonの承認が必要な商品があります。";
    }
    setToastMessage(msg);
    // setToastMessage("プラン作成に失敗しました。");
    setToastSeverity("error");
    setToastOpen(true);
    setError(null);
  }, [error]);

  const getShippingSourceInformation = async (isDialogOpen: boolean) => {
    if (!loginUser?.id) return;
    const req: RequestGetShippingSourceInformationRequest = {
      userId: loginUser?.id,
    };
    const response = await fetchShippingSourceInformation(req);
    if (response) {
      setShippingInformation(response);
    }
    if (isDialogOpen) {
      setCreatePlanDialogOpen(true);
    }
  };

  useEffect(() => {
    getShippingSourceInformation(false);
  }, []);

  const handleRegisterShippingInfo = async (
    shippingSourceInfo: ShippingSourceInfoItem
  ) => {
    const req: RequestUpsertShippingSourceInformationRequest = {
      id: shippingInformation?.id ?? undefined,
      tenantId: 1,
      userId: loginUser?.id!,
      countryId: shippingSourceInfo.countryId,
      postalCode: shippingSourceInfo.postalCode,
      prefectureId: shippingSourceInfo.stateOrProvinceId,
      city: shippingSourceInfo.city,
      addressLine1: shippingSourceInfo.addressLine1,
      name: shippingSourceInfo.name,
      phoneNumber: shippingSourceInfo.phoneNumber,
    };
    const response = await upsertShippingSourceInformation(req);
    if (response) {
      setToastMessage("配送元情報を登録しました。");
      setToastSeverity("success");
      setToastOpen(true);
    }
  };

  const changeSortKey = (
    key: keyof PendingPlanItem | null,
    direction: Order
  ) => {
    if (loginUser?.tenantId && loginUser.id) {
      // --- ソートキーをPendingReceiptItemのkeyから対応するSortKey番号に変換 ---
      const sortKeyMap: Record<keyof PendingPlanItem, number> = {
        inputDate: 1, // a.created_at
        user: 2, // user_name
        itemName: 3, // a.amazon_item_name
        janCode: 4, // i.jan_code
        expiryDate: 5, // i.expiry_date
        setCount: 6, // i.set_num
        deliveryCount: 7, // i.arrive_num
        expectedCount: 8, // i.set_num * i.arrive_num
        receivedCount: 9, // a.sum_num
        fnsku: 10, // a.fnsku_code

        // 他のフィールドは対象外
        id: 0,
        parentId: 0,
        isParent: 0,
        image: 0,
        asin: 0,
        sku: 0,
        stickerNum: 0,
        returnNum: 0,
        destructionNum: 0,
        storageLocation: 0,
        baseId: 0,
        userId: 0,
        memo: 0,
        statusId: 0,
        rowColor: 0,
        isChecked: 0,
        deliveryUnit: 0,
        shippedCount: 0,
        tenantId: 0,
      };

      const sortKey = key ? sortKeyMap[key] ?? 1 : 1;
      const sortDirection = direction === "asc" ? 1 : -1;
      refSortKey.current = sortKey;
      refDirection.current = sortDirection;
      const req: RequestGetAmazonItemsListRequest = {
        statusId: Status.PendingPlan,
        tenantId: loginUser.tenantId,
        userId: loginUser.id,
        createdUserId: selectedUserId ?? undefined,
        janCode:
          searchGenres[0].id && janCode !== "" ? searchKeyword : undefined,
        asin: searchGenres[1].id && asin !== "" ? searchKeyword : undefined,
        amazonItemName:
          searchGenres[2].id && itemName !== "" ? searchKeyword : undefined,
        fnskuCode:
          searchGenres[3].id && fnsku !== "" ? searchKeyword : undefined,
        sku: searchGenres[4].id && sku !== "" ? searchKeyword : undefined,
        page,
        pageSize: limit,
        sortKey,
        direction: sortDirection,
      };
      fetchData(req);
    }
  };

  return {
    inputRef,
    loginUser,
    displayList,
    userOptions,
    selectedUserId,
    handleUserChange,
    checkedIds,
    handleCheckChange,
    searchGenres,
    selectedGenreId,
    setSelectedGenreId,
    focusInputField,
    handleGenreChange,
    searchKeyword,
    setSearchKeyword,
    searchItems,
    resetFilter,
    resetGenreWord,
    handleKeywordChange,
    handleRowClick,
    detailDialogOpen,
    setDetailDialogOpen,
    selectedParent,
    setSelectedParent,
    selectedChildren,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    resetCheckCode,
    fileInputRef,
    handleOpenFileDialog,
    handleFileChange,
    handleCreatePlan,
    handleRegisterShippingInfo,
    createPlanDialogOpen,
    setCreatePlanDialogOpen,
    handlePageChange,
    isLoading,
    getShippingSourceInformation,
    shippingInformation,
    setShippingInformation,
    toastCloseTime,
    totalListCount,
    handleDownloadManifestExcelByUser,
    changeSortKey,
  };
};
